import { curry } from 'lib/util'

/**
 * Escapes `val` correctly if it's a string.
 *
 * @param {CSVPrimitive} val
 * @returns {string}
 */
const escapeValue = (val) => {
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') {
    return `"${val.replace(/"/g, '""')}"` // Wrap and escape quotes
  }
  return String(val)
}

/**
 * @typedef {(string | number | boolean)} CSVPrimitive
 * @typedef {Array<CSVPrimitive>} CSVRow
 * @typedef {Array<CSVRow>} CSVArrayData
 * @typedef {Array<Record<string, CSVPrimitive>>} CSVObjectData
 * @typedef {(CSVArrayData | CSVObjectData)} CSVData
 */

/**
 * Transforms the given `data` into a csv and returns it according to `returnType`,
 * which can be `blob` or `string`.
 *
 * It works with and array of arrays or an array of objects.
 *
 * @example
 * // Array of arrays
 * csv(module, component, { detail: { options: {
 *   delimiter: ',',
 *   columnHeaders: false,
 *   returnType: 'string'
 *   data: [
 *     ['Name', 'Age', 'Active'],
 *     ['Alice', 30, true],
 *     ['Bob', 25, false]
 *   ],
 * }}});
 * // Returns: "Name,Age,Active\r\nAlice,30,true\r\nBob,25,false"
 *
 * @example
 * // Array of objects, with headers
 * csv(module, component, { detail: { options: {
 *   delimiter: ',',
 *   columnHeaders: true,
 *   returnType: 'string'
 *   data: [
 *     { Name: 'Alice', Age: 30, Active: true },
 *     { Name: 'Bob', Age: 25, Active: false }
 *   ],
 * }}})
 * // Returns: "Name,Age,Active\r\nAlice,30,true\r\nBob,25,false"
 *
 * @example
 * // Array of objects, returning as Blob
 * const blob = csv(module, component, { detail: { options: {
 *   delimiter: ';',
 *   columnHeaders: true,
 *   returnType: 'blob'
 *   data :[
 *     { Name: 'Alice', Age: 30, Active: true },
 *     { Name: 'Bob', Age: 25, Active: false }
 *   ],
 * }}})
 * // Returns: Blob object (MIME type: text/csv;charset=utf-8)
 *
 * @param {Gaia.AppModule.Spec} module        module composition
 * @param {Gaia.Component.Spec} component     action component
 * @param {Gaia.AppModule.EventHandler} event triggered event
 *
 * @param {string} event.options.delimiter                        the field delimiter
 *                                                                (e.g., ',' or ';')
 * @param {CSVData} event.options.data                            the input data: either an array of
 *                                                                arrays or an array of objects
 * @param {boolean} [event.options.columnHeaders=true]            whether to include column headers
 *                                                                (only used with object data)
 * @param {'string' | 'blob'} [event.options.returnType='string'] whether to return a CSV string
 *                                                                or a Blob
 * @returns {string | Blob} The generated CSV content.
 */
const csv = (module, component, event) => {
  const {
    data,
    returnType = 'blob',
    options: { delimiter = ',', columnHeaders = false } = {},
  } = event?.detail || event || {}

  // Returning early if we have no data
  if (!Array.isArray(data) || data.length === 0) {
    return returnType === 'blob'
      ? new Blob([''], { type: 'text/csv' })
      : ''
  }

  // Parsing data into csv structure
  const outputRows = Array.isArray(data[0])
    ? data.map(row => row.map(escapeValue).join(delimiter))
    : (() => {
      const headers = Object.keys(data[0])
      const rows = data.map(obj => headers.map(key => escapeValue(obj[key])).join(delimiter))
      return columnHeaders
        ? [headers.map(escapeValue).join(delimiter), ...rows]
        : rows
    })()

  // Spec explicitly states that rows should be ended with a CRLF.
  // https://datatracker.ietf.org/doc/html/rfc4180#section-2
  const output = outputRows.join('\r\n')

  return returnType === 'blob'
    ? new Blob([output], { type: 'text/csv' })
    : output
}

export default curry(csv)
